/* ==========================================================================
   Apps Catalog Data - Nimbokit Ecosystem Definition
   ========================================================================== */

export type AppCategory = 'focus' | 'creative' | 'automation';
export type PlatformType = 'Web App' | 'Desktop App' | 'Python CLI' | 'Chrome Extension';

export interface AppFeature {
  id: string;
  metric: string;
  label: string;
  description: string;
  en: {
    metric: string;
    label: string;
    description: string;
  };
}

export interface AppStatItem {
  num: string;
  label: string;
  en: {
    num: string;
    label: string;
  };
}

export interface AppItem {
  id: string;
  name: string;
  headline: string;
  tagline: string;
  description: string;
  category: AppCategory;
  platform: PlatformType;
  mascotPose: string;
  previewShot: string;
  featured: boolean;
  badges: string[];
  metrics?: string;
  features?: AppFeature[];
  chips: string[];
  stats: AppStatItem[];
  links: {
    app: string;        // Link truy cập app trực tiếp hoặc tải xuống
    appLabel?: string;  // Nhãn nút chính (mặc định: 'Truy cập ứng dụng')
    landing?: string;   // Link trang giới thiệu (Landing Page)
    landingLabel?: string;
  };
  en?: {
    name?: string;
    headline: string;
    tagline: string;
    description: string;
    badges: string[];
    chips: string[];
    appLabel?: string;
    landingLabel?: string;
  };
}

export const APPS_DATA: AppItem[] = [
  {
    id: 'mindmap',
    name: 'IdeaMap',
    headline: 'Sơ Đồ Tư Duy & AI Trên Máy Tính',
    tagline: 'Sơ đồ tư duy trực quan, lưu trên máy tính & trợ lý AI',
    description: 'Sơ đồ tư duy Local-First bảo mật trên máy tính. AI qua API riêng, đồng bộ Google Drive, không phí thuê bao.',
    category: 'focus',
    platform: 'Web App',
    mascotPose: 'mascot_deep_focus.png',
    previewShot: 'ideamap-showcase.png',
    featured: true,
    badges: ['Local-First', 'Trợ lý AI', 'Bản quyền trọn đời'],
    chips: ['Vẽ mượt vô cực', 'Lưu trên máy tính', 'AI API riêng', 'Bản quyền trọn đời'],
    stats: [
      { num: '0đ', label: 'Thuê bao', en: { num: '0$', label: 'Subscription' } },
      { num: '100%', label: 'Offline', en: { num: '100%', label: 'Offline' } },
      { num: '60 FPS', label: 'Mượt mà', en: { num: '60 FPS', label: 'Fluidity' } }
    ],
    features: [
      {
        id: 'canvas',
        metric: '60 FPS',
        label: 'Mượt mà vô cực',
        description: 'Vẽ ý tưởng không giới hạn, phóng to thu nhỏ cực mượt.',
        en: {
          metric: '60 FPS',
          label: 'Fluid Infinite Canvas',
          description: 'Limitless visual canvas with ultra-smooth 60fps pan & zoom.'
        }
      },
      {
        id: 'offline',
        metric: '100% Offline',
        label: 'Lưu trên máy tính',
        description: 'Dữ liệu của bạn nằm an toàn trên máy, không lo rò rỉ.',
        en: {
          metric: '100% Offline',
          label: 'Private Local Storage',
          description: '100% private data stored safely on your machine.'
        }
      },
      {
        id: 'ai',
        metric: 'Trợ lý AI',
        label: 'Kết nối API riêng',
        description: 'Tùy chọn mở rộng ý tưởng qua Google Gemini, OpenAI miễn phí.',
        en: {
          metric: 'Custom AI',
          label: 'BYO-Key Assistant',
          description: 'Brainstorm ideas using your own free Gemini or OpenAI key.'
        }
      },
      {
        id: 'license',
        metric: '0đ Định kỳ',
        label: 'Bản quyền trọn đời',
        description: 'Dùng mãi mãi, hoàn toàn không thu phí thuê bao hàng tháng.',
        en: {
          metric: '$0/mo Rent',
          label: 'Lifetime Ownership',
          description: 'Zero monthly subscriptions, owned forever.'
        }
      }
    ],
    links: {
      app: 'https://ideamap.nimbokit.com',
      appLabel: 'Dùng thử miễn phí',
      landing: 'https://ideamap.nimbokit.com/about',
      landingLabel: 'Giới thiệu ↗'
    },
    en: {
      headline: 'Mind Mapping & AI On Your Machine',
      tagline: 'Visual mind mapping, local-first on your machine with custom AI',
      description: 'Local-first mind mapping on your machine. Private AI via API, Google Drive sync, zero subscription.',
      badges: ['Local-First', 'AI Assistant', 'Lifetime License'],
      chips: ['Infinite Canvas', 'Local-first Storage', 'Custom AI API', 'Lifetime License'],
      appLabel: 'Free Trial',
      landingLabel: 'About ↗'
    }
  },
  {
    id: 'multi-tasks-autoclick',
    name: 'Multi-Task AutoClick',
    headline: 'Tự Động Hóa Chuột & Phím Như Người Thật',
    tagline: 'Tự động hóa chuột & bàn phím đa nhiệm như người thật trên Windows',
    description: 'Tự động hóa chuột & bàn phím trên Windows. Đa luồng song song, chạy offline 100%, không độ trễ.',
    category: 'automation',
    platform: 'Desktop App',
    mascotPose: 'mascot_settings.png',
    previewShot: 'autoclick-showcase.png',
    featured: false,
    badges: ['Offline 100%', 'Đa luồng song song', 'Chuột & Phím'],
    chips: ['Click & phím đa điểm', 'Lặp vô hạn', 'Delay ngẫu nhiên'],
    stats: [
      { num: 'Miễn phí', label: 'Dùng thử', en: { num: 'Free', label: 'Trial' } },
      { num: '100%', label: 'Offline', en: { num: '100%', label: 'Offline' } },
      { num: '0ms', label: 'Độ trễ', en: { num: '0ms', label: 'Latency' } }
    ],
    features: [
      {
        id: 'multithread',
        metric: 'Đa luồng',
        label: 'Chạy song song',
        description: 'Xử lý nhiều vị trí click đồng thời mà không bị nghẽn.',
        en: {
          metric: 'Multi-Thread',
          label: 'Parallel Execution',
          description: 'Run multiple click routines simultaneously without bottlenecks.'
        }
      },
      {
        id: 'human',
        metric: 'Tự nhiên',
        label: 'Như người thật',
        description: 'Khoảng trễ và tọa độ dao động tự nhiên, tránh bị chặn spam.',
        en: {
          metric: 'Human Cadence',
          label: 'Natural Random Jitter',
          description: 'Random delays and gentle jitter mimicking real human hands.'
        }
      },
      {
        id: 'offline',
        metric: '100% Offline',
        label: 'An toàn bảo mật',
        description: 'Không cần internet, bảo mật tuyệt đối cho máy tính của bạn.',
        en: {
          metric: '100% Offline',
          label: 'Zero-Network Risk',
          description: 'Zero internet required, 100% private and secure.'
        }
      },
      {
        id: 'presets',
        metric: 'Kịch bản',
        label: 'Lưu & nạp nhanh',
        description: 'Lưu cấu hình thành file để mở lại dùng ngay cho từng công việc.',
        en: {
          metric: 'Instant Profiles',
          label: 'Save & Replay Scripts',
          description: 'Save click routines into instant profiles for repeated workflows.'
        }
      }
    ],
    links: {
      app: 'https://multitasksautoclick.nimbokit.com/downloads/MultiTaskAutoClick_Setup.exe',
      appLabel: 'Tải dùng thử',
      landing: 'https://multitasksautoclick.nimbokit.com',
      landingLabel: 'Giới thiệu ↗'
    },
    en: {
      headline: 'Automate Mouse & Keyboard Like A Human',
      tagline: 'Multi-task mouse & keyboard automation like a real human on Windows',
      description: 'Automate repetitive tasks on Windows. Multi-threaded execution, 100% offline, zero latency.',
      badges: ['100% Offline', 'Multi-Threaded', 'Mouse & Key'],
      chips: ['Multi-point clicks & keys', 'Infinite loops', 'Human-like delays'],
      appLabel: 'Download Free',
      landingLabel: 'About ↗'
    }
  }
];

export interface TrustPrinciple {
  id: string;
  iconSvg: string;
  title: string;
  description: string;
  en: {
    title: string;
    description: string;
  };
}

export const TRUST_PRINCIPLES: TrustPrinciple[] = [
  {
    id: 'local-first',
    iconSvg: '<svg class="trust-ic" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>',
    title: '100% Local-First',
    description: 'Dữ liệu lưu an toàn trên máy tính. Hoạt động offline, không gửi dữ liệu ra máy chủ ngoài.',
    en: {
      title: '100% Local-First',
      description: 'Data stays safely on your machine. 100% offline, zero cloud reliance.'
    }
  },
  {
    id: 'lifetime-license',
    iconSvg: '<svg class="trust-ic" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>',
    title: 'Bản Quyền Trọn Đời',
    description: 'Sở hữu vĩnh viễn, không phí thuê bao. Miễn phí mọi bản cập nhật tương lai.',
    en: {
      title: 'Lifetime Ownership',
      description: 'Own forever, zero subscription. Free updates and bug fixes included.'
    }
  },
  {
    id: 'private-ai',
    iconSvg: '<svg class="trust-ic" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
    title: 'Tự Chủ & Bảo Mật API',
    description: 'Dùng trực tiếp API Key cá nhân. Không bán credit trung gian, không lưu trữ token.',
    en: {
      title: 'Private & Direct API',
      description: 'Direct connection via your API keys. No credit markup, zero token storage.'
    }
  }
];

